import Bugsnag from "@bugsnag/js";
import { supabase } from "@/lib/supabase";

/**
 * Platform admin creates an account and ties it to a district, optionally to a
 * school in that district.
 *
 * The server (`supabase/functions/admin-create-user`) owns every rule: the
 * district email-domain match including final-plus routing, seat availability,
 * and the write order that makes the account show up in the Accounts list. This
 * module is transport plus error mapping — it deliberately re-derives nothing,
 * so there is no second implementation of the domain rule to drift.
 */

export type AdminUserPersona = "leader" | "district_leader" | "teacher" | "support_staff";

export interface CreateAdminUserInput {
  email: string;
  districtId: string;
  /** null creates a district-level user with no school. */
  schoolId: string | null;
  persona: AdminUserPersona;
  /** Left blank, the server falls back to an email-derived placeholder. */
  firstName?: string | null;
  lastName?: string | null;
}

export interface CreatedAdminUser {
  userId: string;
  email: string;
  userName: string;
  persona: AdminUserPersona;
  districtId: string;
  districtName: string;
  schoolId: string | null;
  schoolName: string | null;
  role: "principal" | "teacher";
}

export type CreateAdminUserResult =
  | { ok: true; user: CreatedAdminUser }
  | { ok: false; errorCode: string; errorMessage: string };

interface EdgeFailure {
  ok: false;
  error?: string;
  error_code?: string;
}

interface EdgeSuccess {
  ok: true;
  user: {
    user_id: string;
    email: string;
    user_name: string;
    workspace_persona: AdminUserPersona;
    district_id: string;
    district_name: string;
    school_id: string | null;
    school_name: string | null;
    role: "principal" | "teacher";
  };
}

/**
 * Failures the UI presents verbatim. Anything outside this set is a surprise
 * worth a Bugsnag report, matching `inviteTeammatesService`'s split between
 * expected product outcomes and genuine faults.
 */
const EXPECTED_ERROR_CODES = new Set([
  "invalid_email",
  "invalid_persona",
  "missing_district",
  "district_not_found",
  "school_not_found",
  "school_outside_district",
  "domain_mismatch",
  "domain_not_configured",
  "invalid_domain_email",
  "already_exists",
  "seat_full",
  "forbidden",
  // A session expiring while the modal is open is routine, not a surprise —
  // matches the sibling inviteTeammatesService's own "unauthorized" code.
  "unauthorized",
]);

const notifyUnexpected = (context: string, err: unknown) => {
  Bugsnag.notify(err instanceof Error ? err : new Error(String(err)), (event) => {
    event.severity = "error";
    event.context = context;
  });
};

/**
 * A refused create is a real non-2xx, and supabase-js hands that back as an
 * error carrying the original Response on `context` with `data` left null. The
 * body is the only place the error_code and the explanatory message exist.
 */
async function readEdgeFailure(invokeResult: {
  data: unknown;
  error: unknown;
}): Promise<EdgeFailure | null> {
  const inline = invokeResult.data as EdgeFailure | null;
  if (inline && inline.ok === false) return inline;

  const response = (invokeResult.error as { context?: Response } | null)?.context;
  if (!response || typeof response.clone !== "function") return null;

  try {
    const text = await response.clone().text();
    if (!text) return null;
    return JSON.parse(text) as EdgeFailure;
  } catch {
    return null;
  }
}

export async function createAdminUser(
  input: CreateAdminUserInput,
): Promise<CreateAdminUserResult> {
  const email = input.email.trim().toLowerCase();
  const firstName = input.firstName?.trim() || null;
  const lastName = input.lastName?.trim() || null;

  const body = {
    email,
    district_id: input.districtId,
    school_id: input.schoolId,
    persona: input.persona,
    first_name: firstName,
    last_name: lastName,
  };

  try {
    const invokeResult = await supabase.functions.invoke("admin-create-user", { body });

    const success = invokeResult.data as EdgeSuccess | null;
    if (success?.ok) {
      const row = success.user;
      return {
        ok: true,
        user: {
          userId: row.user_id,
          email: row.email,
          userName: row.user_name,
          persona: row.workspace_persona,
          districtId: row.district_id,
          districtName: row.district_name,
          schoolId: row.school_id,
          schoolName: row.school_name,
          role: row.role,
        },
      };
    }

    const failure = await readEdgeFailure(invokeResult);
    if (failure) {
      const errorCode = failure.error_code ?? "internal";
      const errorMessage = failure.error ?? "Could not create the account.";
      if (!EXPECTED_ERROR_CODES.has(errorCode)) {
        notifyUnexpected(
          "adminCreateUserService.createAdminUser",
          new Error(`${errorCode}: ${errorMessage}`),
        );
      }
      return { ok: false, errorCode, errorMessage };
    }

    const message =
      (invokeResult.error as { message?: string } | null)?.message ??
      "Could not reach the account service.";
    notifyUnexpected("adminCreateUserService.createAdminUser", new Error(message));
    return { ok: false, errorCode: "internal", errorMessage: message };
  } catch (err) {
    notifyUnexpected("adminCreateUserService.createAdminUser", err);
    return {
      ok: false,
      errorCode: "internal",
      errorMessage: "Something went wrong. Please try again.",
    };
  }
}
